import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaPlay, FaPlus, FaCheck } from 'react-icons/fa';
import { MovieContext } from '../context/Movies.context';

const FALLBACK_POSTER =
  'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=500&q=80';

const formatOttDate = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const OttCard = (props) => {
  const { toggleMyList, isInMyList, openWatch } = useContext(MovieContext);
  const [hasError, setHasError] = useState(false);

  const rawPoster = props.poster_path;
  const posterUrl = rawPoster
    ? rawPoster.startsWith('http')
      ? rawPoster
      : `https://image.tmdb.org/t/p/w500${rawPoster}`
    : FALLBACK_POSTER;

  useEffect(() => {
    setHasError(false);
  }, [props.id, props.poster_path]);

  const title = props.title || props.original_title || 'Untitled';
  const rating = props.vote_average ? Number(props.vote_average).toFixed(1) : '';
  const isSaved = isInMyList ? isInMyList(props.id) : false;
  const ottDate = formatOttDate(props.ott_date);

  return (
    <div
      className="movie-card flex flex-col items-start gap-2 px-1.5 py-2 group w-full max-w-[220px]"
      data-focusable
      tabIndex={0}
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden rounded-xl bg-dark-700 border border-white/5 shadow-lg group-hover:border-accent-gold/40 transition-all duration-300">
        <Link to={`/movie/${props.id}`} className="block w-full h-full">
          <img
            key={props.id ? `ott-poster-${props.id}` : posterUrl}
            src={hasError ? FALLBACK_POSTER : posterUrl}
            alt={title}
            onError={() => setHasError(true)}
            className="w-full h-full object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </Link>

        {props.ott_new && (
          <span className="absolute top-2.5 left-2.5 bg-emerald-500 text-dark-900 text-[10px] font-black tracking-wider px-2 py-1 rounded-lg shadow-md">
            NEW
          </span>
        )}

        {rating && (
          <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/75 backdrop-blur-md text-accent-gold text-xs font-bold px-2 py-1 rounded-lg border border-white/10 shadow-md">
            <FaStar className="w-3 h-3" />
            <span>{rating}</span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-dark-900/95 via-dark-900/30 to-transparent opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-300 rounded-xl flex flex-col justify-end p-3 pointer-events-none">
          <div className="flex items-center gap-2 pointer-events-auto">
            <button
              onClick={() => openWatch && openWatch(props)}
              title="Watch Now"
              className="flex-1 flex items-center justify-center gap-1.5 bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-bold text-xs py-2 rounded-lg transition-all shadow-md"
            >
              <FaPlay className="w-2.5 h-2.5" />
              <span>Watch</span>
            </button>
            <button
              onClick={() => toggleMyList && toggleMyList(props)}
              title={isSaved ? 'Remove from List' : 'Add to List'}
              className={`p-2 rounded-lg transition-colors border ${
                isSaved
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                  : 'bg-white/10 hover:bg-white/20 text-white border-white/10'
              }`}
            >
              {isSaved ? <FaCheck className="w-3 h-3" /> : <FaPlus className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>

      <Link to={`/movie/${props.id}`} className="w-full">
        {props.language_label ? (
          <span className="inline-block mb-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-400/90">
            {props.language_label}
          </span>
        ) : null}
        <h3 className="text-sm font-semibold text-gray-200 group-hover:text-accent-gold transition-colors duration-200 line-clamp-1 w-full text-left">
          {title}
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5 text-left line-clamp-1">
          {props.ott_name ? (
            <>
              <span className="text-accent-gold font-semibold">{props.ott_name}</span>
              {ottDate ? <span className="text-gray-500"> · {ottDate}</span> : null}
            </>
          ) : (
            ottDate || 'OTT date TBA'
          )}
        </p>
      </Link>
    </div>
  );
};

export default OttCard;
